import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, Flex, TextField, Typography } from "flux";
import Page from "@adora/shared/components/layout/page";
import {
  BrandOnboardingResponse,
  useAdvanceOnboarding,
  useBrandOnboarding,
  useBrandOnboardingList,
  useCreateBrandOnboarding,
  useSendWelcomeEmails
} from "../../hooks/use-brand-onboarding";

interface UserRow {
  first_name: string;
  last_name: string;
  email: string;
}

const slugify = (name: string): string =>
  name
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/(?:^-|-$)/g, "");

const STEP_LABELS: Record<string, string> = {
  step_create_brand: "Create Brand",
  step_iac_pr: "IAC PR (S3 + CDN)",
  step_iac_merge: "IAC PR Merged",
  step_set_s3_bucket: "Set S3 Bucket",
  step_delorean_pr: "System Prompt PR",
  step_create_users: "Create Users"
};

const STATUS_COLORS: Record<string, string> = {
  pending: "#94a3b8",
  in_progress: "#3b82f6",
  completed: "#22c55e",
  failed: "#ef4444",
  skipped: "#94a3b8"
};

const STATUS_ICONS: Record<string, string> = {
  pending: "\u25CB", // circle outline
  in_progress: "\u25D4", // half circle
  completed: "\u2713", // checkmark
  failed: "\u2717", // x mark
  skipped: "\u2014" // dash
};

const StepStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      px: "8px",
      py: "2px",
      borderRadius: "12px",
      bgcolor: `${STATUS_COLORS[status]}20`,
      color: STATUS_COLORS[status],
      fontSize: "12px",
      fontWeight: 600
    }}
  >
    {STATUS_ICONS[status]} {status.replace("_", " ")}
  </Box>
);

// ─── Create Form ────────────────────────────────────────

const CreateOnboardingForm: React.FC<{ onCreated: (id: number) => void }> = ({ onCreated }) => {
  const [brandName, setBrandName] = useState("");
  const [brandSlug, setBrandSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [description, setDescription] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [users, setUsers] = useState<UserRow[]>([{ first_name: "", last_name: "", email: "" }]);

  const createMutation = useCreateBrandOnboarding();

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const name = e.target.value;
      setBrandName(name);
      if (!slugManuallyEdited) {
        setBrandSlug(slugify(name));
      }
    },
    [slugManuallyEdited]
  );

  const handleSlugChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setBrandSlug(e.target.value);
    setSlugManuallyEdited(true);
  }, []);

  const handleUserChange = useCallback((index: number, field: keyof UserRow, value: string) => {
    setUsers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const addUserRow = useCallback(() => {
    setUsers((prev) => [...prev, { first_name: "", last_name: "", email: "" }]);
  }, []);

  const removeUserRow = useCallback((index: number) => {
    setUsers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const validUsers = users.filter((u) => u.email.trim());
      const result = await createMutation.mutateAsync({
        brand_name: brandName,
        brand_slug: brandSlug || slugify(brandName),
        brand_description: description,
        brand_website_url: websiteUrl,
        users: validUsers
      });
      onCreated(result.id);
    },
    [brandName, brandSlug, description, websiteUrl, users, createMutation, onCreated]
  );

  return (
    <form onSubmit={handleSubmit}>
      <Flex flexDirection="column" gap="16px">
        <Flex gap="16px">
          <Box flex={1}>
            <Typography variant="body2" sx={{ mb: "4px", fontWeight: 600 }}>
              Brand Name *
            </Typography>
            <TextField
              value={brandName}
              onChange={handleNameChange}
              placeholder="e.g. LoveShackFancy"
              required
              fullWidth
              dataDogIdentifier="onboarding-brand-name"
            />
          </Box>
          <Box flex={1}>
            <Typography variant="body2" sx={{ mb: "4px", fontWeight: 600 }}>
              Brand Slug
            </Typography>
            <TextField
              value={brandSlug}
              onChange={handleSlugChange}
              placeholder="auto-generated from name"
              fullWidth
              dataDogIdentifier="onboarding-brand-slug"
            />
          </Box>
        </Flex>

        <Box>
          <Typography variant="body2" sx={{ mb: "4px", fontWeight: 600 }}>
            Description
          </Typography>
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief brand description"
            fullWidth
            dataDogIdentifier="onboarding-description"
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ mb: "4px", fontWeight: 600 }}>
            Brand Website URL
          </Typography>
          <TextField
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            placeholder="https://www.example.com"
            fullWidth
            dataDogIdentifier="onboarding-website-url"
          />
          <Typography variant="caption" color="textSecondary">
            Used for researching the brand to generate the creative assistant prompt
          </Typography>
        </Box>

        <Box>
          <Flex justifyContent="space-between" alignItems="center" mb="8px">
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Users (optional)
            </Typography>
            <Button variant="text" size="small" onClick={addUserRow} dataDogIdentifier="onboarding-add-user">
              + Add User
            </Button>
          </Flex>
          <Flex flexDirection="column" gap="8px">
            {users.map((user, idx) => (
              <Flex key={idx} gap="8px" alignItems="center">
                <TextField
                  value={user.first_name}
                  onChange={(e) => handleUserChange(idx, "first_name", e.target.value)}
                  placeholder="First Name"
                  size="small"
                  sx={{ flex: 1 }}
                  dataDogIdentifier="onboarding-user-first-name"
                />
                <TextField
                  value={user.last_name}
                  onChange={(e) => handleUserChange(idx, "last_name", e.target.value)}
                  placeholder="Last Name"
                  size="small"
                  sx={{ flex: 1 }}
                  dataDogIdentifier="onboarding-user-last-name"
                />
                <TextField
                  value={user.email}
                  onChange={(e) => handleUserChange(idx, "email", e.target.value)}
                  placeholder="Email"
                  size="small"
                  sx={{ flex: 2 }}
                  dataDogIdentifier="onboarding-user-email"
                />
                {users.length > 1 && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => removeUserRow(idx)}
                    sx={{ minWidth: "32px", color: "#ef4444" }}
                    dataDogIdentifier="onboarding-remove-user"
                  >
                    x
                  </Button>
                )}
              </Flex>
            ))}
          </Flex>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={!brandName.trim() || createMutation.isPending}
          dataDogIdentifier="onboarding-create-brand"
          sx={{ alignSelf: "flex-start", mt: "8px" }}
        >
          {createMutation.isPending ? "Creating..." : "Create Trial Brand"}
        </Button>

        {createMutation.isError && (
          <Typography variant="body2" sx={{ color: "#ef4444" }}>
            Error: {createMutation.error?.message}
          </Typography>
        )}
      </Flex>
    </form>
  );
};

// ─── Progress View ──────────────────────────────────────

const OnboardingProgress: React.FC<{
  onboarding: BrandOnboardingResponse;
}> = ({ onboarding }) => {
  const advanceMutation = useAdvanceOnboarding();
  const sendEmailsMutation = useSendWelcomeEmails();
  const steps = onboarding.steps;
  const emailsSent = (onboarding.users || []).some((u) => u.email_sent);
  const hasUsersWithLinks = (onboarding.users || []).some((u) => u.share_link);

  return (
    <Box
      sx={{
        border: "1px solid rgb(226 232 240)",
        borderRadius: "8px",
        bgcolor: "white",
        p: "20px",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
      }}
    >
      <Flex justifyContent="space-between" alignItems="center" mb="16px">
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {onboarding.brand_name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            ID: {onboarding.brand_id ?? "pending"} | Slug: {onboarding.brand_slug}
          </Typography>
        </Box>
        <Flex gap="8px" alignItems="center">
          <StepStatusBadge status={onboarding.status} />
          {onboarding.status === "in_progress" && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => advanceMutation.mutate(onboarding.id)}
              disabled={advanceMutation.isPending}
              dataDogIdentifier="onboarding-refresh-status"
            >
              Refresh Status
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex flexDirection="column" gap="10px">
        {Object.entries(STEP_LABELS).map(([key, label]) => {
          const status = steps[key as keyof typeof steps];
          return (
            <Flex
              key={key}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                py: "6px",
                px: "12px",
                bgcolor: status === "completed" ? "#f0fdf4" : "transparent",
                borderRadius: "6px"
              }}
            >
              <Typography variant="body2">{label}</Typography>
              <StepStatusBadge status={status} />
            </Flex>
          );
        })}
      </Flex>

      {(onboarding.iac_pr_url || onboarding.delorean_pr_url) && (
        <Box
          mt="16px"
          sx={{
            p: "12px",
            bgcolor: "#eff6ff",
            borderRadius: "6px",
            border: "1px solid #bfdbfe"
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: "8px" }}>
            PRs are ready for review. Please get these approved by the engineering team:
          </Typography>
          {onboarding.iac_pr_url && (
            <Box mb="4px">
              <Typography variant="body2">
                Infrastructure (S3 + CDN):{" "}
                <a href={onboarding.iac_pr_url} target="_blank" rel="noreferrer">
                  {onboarding.iac_pr_url}
                </a>
              </Typography>
            </Box>
          )}
          {onboarding.delorean_pr_url && (
            <Box>
              <Typography variant="body2">
                System Prompt:{" "}
                <a href={onboarding.delorean_pr_url} target="_blank" rel="noreferrer">
                  {onboarding.delorean_pr_url}
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {hasUsersWithLinks && (
        <Box mt="16px">
          <Button
            variant="contained"
            onClick={() => sendEmailsMutation.mutate(onboarding.id)}
            disabled={onboarding.status !== "completed" || emailsSent || sendEmailsMutation.isPending}
            dataDogIdentifier="onboarding-send-emails"
            sx={{
              bgcolor: emailsSent ? "#22c55e" : undefined,
              "&:disabled": {
                bgcolor: onboarding.status !== "completed" ? undefined : emailsSent ? "#22c55e" : undefined,
                color: emailsSent ? "#fff" : undefined
              }
            }}
          >
            {sendEmailsMutation.isPending ? "Sending..." : emailsSent ? "Welcome Emails Sent" : "Send Welcome Emails"}
          </Button>
          {onboarding.status !== "completed" && !emailsSent && (
            <Typography variant="caption" color="textSecondary" sx={{ ml: "8px" }}>
              Available once all steps are complete
            </Typography>
          )}
        </Box>
      )}

      {onboarding.error_message && (
        <Box
          mt="12px"
          sx={{
            p: "8px",
            bgcolor: "#fef2f2",
            borderRadius: "6px",
            border: "1px solid #fecaca"
          }}
        >
          <Typography variant="caption" sx={{ color: "#ef4444" }}>
            {onboarding.error_message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Main Page ──────────────────────────────────────────

const BrandOnboardingPage: React.FC = () => {
  const [activeOnboardingId, setActiveOnboardingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(true);

  const { data: onboardings } = useBrandOnboardingList();
  const { data: activeOnboarding } = useBrandOnboarding(activeOnboardingId);

  const handleCreated = useCallback((id: number) => {
    setActiveOnboardingId(id);
    setShowForm(false);
  }, []);

  const recentOnboardings = useMemo(() => (onboardings ?? []).slice(0, 10), [onboardings]);

  return (
    <Page title="Create Trial Brand">
      <Flex flexDirection="column" gap="24px" width="100%" maxWidth="800px" mx="auto">
        {showForm ? (
          <Box
            sx={{
              border: "1px solid rgb(226 232 240)",
              borderRadius: "8px",
              bgcolor: "white",
              p: "24px",
              boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: "16px" }}>
              New Trial Brand
            </Typography>
            <CreateOnboardingForm onCreated={handleCreated} />
          </Box>
        ) : (
          <Button
            variant="outlined"
            onClick={() => setShowForm(true)}
            dataDogIdentifier="onboarding-new-brand"
            sx={{ alignSelf: "flex-start" }}
          >
            + New Brand
          </Button>
        )}

        {activeOnboarding && <OnboardingProgress onboarding={activeOnboarding} />}

        {recentOnboardings.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: "12px" }}>
              Recent Onboardings
            </Typography>
            <Flex flexDirection="column" gap="8px">
              {recentOnboardings.map((o) => (
                <Flex
                  key={o.id}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    py: "8px",
                    px: "12px",
                    border: "1px solid rgb(226 232 240)",
                    borderRadius: "6px",
                    bgcolor: activeOnboardingId === o.id ? "#f1f5f9" : "white",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#f8fafc" }
                  }}
                  onClick={() => {
                    setActiveOnboardingId(o.id);
                    setShowForm(false);
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {o.brand_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {o.brand_slug} | Brand ID: {o.brand_id ?? "pending"}
                    </Typography>
                  </Box>
                  <StepStatusBadge status={o.status} />
                </Flex>
              ))}
            </Flex>
          </Box>
        )}
      </Flex>
    </Page>
  );
};

export default BrandOnboardingPage;
